import type { HttpPort } from "~/lib/http";

export type FeedbackTab = "praise" | "concerns";
export type PraiseReaction = "heart" | "fire" | "clap" | "star" | "muscle";

interface RawPraise {
  id: string;
  message: string;
  student_name: string | null;
  created_at: string;
  heart_count: number;
  fire_count: number;
  clap_count: number;
  star_count: number;
  muscle_count: number;
  my_likes?: PraiseReaction[];
}

interface RawConcern {
  id: string;
  message: string;
  is_anonymous: boolean;
  is_read: boolean;
  student_name: string | null;
  created_at: string;
}

export interface PraiseMessage {
  id: string;
  message: string;
  studentName: string;
  dateLabel: string;
  reactions: Record<PraiseReaction, number>;
  myReactions: PraiseReaction[];
}

export interface ConcernMessage {
  id: string;
  message: string;
  anonymous: boolean;
  read: boolean;
  senderLabel: string;
  dateLabel: string;
}

export type FeedbackView =
  | { tab: "praise"; messages: PraiseMessage[] }
  | { tab: "concerns"; messages: ConcernMessage[]; unread: ConcernMessage[]; read: ConcernMessage[]; unreadCount: number };

export type FeedbackCommand =
  | { type: "react"; praiseId: string; reaction: PraiseReaction }
  | { type: "delete-praise"; praiseId: string }
  | { type: "read-concern"; concernId: string }
  | { type: "delete-concern"; concernId: string };

export interface ReactionResult {
  counts: Partial<Record<PraiseReaction, number>>;
  myLikes: PraiseReaction[];
}

function shortDate(value: string) {
  return new Date(value).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function fullDate(value: string) {
  return new Date(value).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function praiseView(value: RawPraise): PraiseMessage {
  return {
    id: value.id,
    message: value.message,
    studentName: value.student_name || "Schüler",
    dateLabel: shortDate(value.created_at),
    reactions: {
      heart: Number(value.heart_count) || 0,
      fire: Number(value.fire_count) || 0,
      clap: Number(value.clap_count) || 0,
      star: Number(value.star_count) || 0,
      muscle: Number(value.muscle_count) || 0
    },
    myReactions: value.my_likes ?? []
  };
}

function concernView(value: RawConcern): ConcernMessage {
  return {
    id: value.id,
    message: value.message,
    anonymous: value.is_anonymous,
    read: value.is_read,
    senderLabel: value.is_anonymous ? "Anonym" : value.student_name || "Unbekannt",
    dateLabel: fullDate(value.created_at)
  };
}

export function createFeedbackModule(http: HttpPort, token: string) {
  return {
    async load(classId: string, tab: FeedbackTab): Promise<FeedbackView> {
      if (tab === "praise") {
        const result = await http.request<{ praise?: RawPraise[] }>({ path: `/feedback/${classId}/praise`, token });
        return { tab, messages: (result.praise ?? []).map(praiseView) };
      }
      const result = await http.request<{ concerns?: RawConcern[] }>({ path: `/feedback/${classId}/concerns`, token });
      const messages = (result.concerns ?? []).map(concernView);
      const unread = messages.filter((message) => !message.read);
      const read = messages.filter((message) => message.read);
      return { tab, messages, unread, read, unreadCount: unread.length };
    },

    async countUnread(classId: string): Promise<number> {
      const result = await http.request<{ count?: number }>({ path: `/feedback/${classId}/concerns/unread-count`, token });
      return Number(result.count) || 0;
    },

    async execute(classId: string, command: FeedbackCommand): Promise<ReactionResult | void> {
      if (command.type === "react") {
        return http.request<ReactionResult>({
          path: `/feedback/${classId}/praise/${command.praiseId}/like`,
          method: "POST",
          token,
          body: { like_type: command.reaction }
        });
      }
      if (command.type === "delete-praise") {
        await http.request({ path: `/feedback/${classId}/praise/${command.praiseId}`, method: "DELETE", token });
        return;
      }
      if (command.type === "read-concern") {
        await http.request({ path: `/feedback/${classId}/concerns/${command.concernId}/read`, method: "PATCH", token, body: {} });
        return;
      }
      await http.request({ path: `/feedback/${classId}/concerns/${command.concernId}`, method: "DELETE", token });
    }
  };
}

export type FeedbackModule = ReturnType<typeof createFeedbackModule>;
